import React, { useEffect } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import IApplicationReviewSummary from 'interfaces/IApplicationReviewSummary';
import { ReviewerRole, ReviewFormat } from '../../../graphql/__generated__/graphql';
import useApplicationReviews from './useApplicationReviews';
import _ from 'lodash';

const ReviewsBlock: React.FC = () => {
    const { models, operations } = useApplicationReviews();
    const { applicationReviews } = models;

    const navigate = useNavigate();

    const handleOnSelectReview = (reviewSummary: IApplicationReviewSummary) => {
        navigate(`/accreditation/review/${reviewSummary.applicationReviewId}`);
    };

    useEffect(() => {
        operations.refetch({
            fetchPolicy: 'no-cache',
        });
    }, [operations]);

    if (!applicationReviews || applicationReviews.length === 0) {
        return <></>;
    }

    const sortedReviews = _.sortBy(applicationReviews, 'requestedDate', 'reviewerRole');

    return (
        <>
            <Row className={'col-9'}>
                <h2 className="fs-3 fw-bold mb-3">Reviews</h2>
            </Row>
            {sortedReviews?.map((review) => (
                <Row key={review.applicationReviewId} className={'mb-3'}>
                    <ReviewBlock
                        review={review}
                        handleOnSelectReview={() => handleOnSelectReview(review)}
                    />
                </Row>
            ))}
        </>
    );
};

interface ReviewBlockProps {
    review: IApplicationReviewSummary;
    handleOnSelectReview: (review: IApplicationReviewSummary) => void;
}

const ReviewBlock: React.FC<ReviewBlockProps> = ({ review, handleOnSelectReview }) => {
    const isPrimary = review.reviewerRole === ReviewerRole.PrimarySupervisor;

    const niceReviewFormat = () => {
        switch (review.reviewFormat) {
            case ReviewFormat.Algorithm:
                return 'Algorithm';
            case ReviewFormat.Descriptive:
                return 'Descriptive';
            default:
                return null;
        }
    };

    return (
        <Card className={`border-0 w-75 pb-2`}>
            <CardHeader className={`bg-transparent border-0 p-0`}>
                <div className={'end-pill-wrapper'}>
                    <Badge pill bg={'review-status'} className={'floating-pill'}>
                        Requested: {new Date(review.requestedDate).toLocaleDateString()}
                    </Badge>
                </div>
            </CardHeader>
            <CardBody className={'mb-0'}>
                <dl className={'d-flex mb-0'}>
                    <dt>{isPrimary ? 'Evaluation' : 'Sessions confirmation'}:</dt>
                    <dd className={'ms-1'}>{review.applicant.name}</dd>
                </dl>
                <dl className={'d-flex mb-0'}>
                    <dt>{review.isRenewal ? 'Reaccreditation' : 'Accreditation'}:</dt>
                    <dd className={'ms-1'}>{review.accreditationType.name}</dd>
                </dl>
                {isPrimary && review.reviewFormat && (
                    <dl className={'d-flex mb-0'}>
                        <dt>Review Type</dt>
                        <dd className={'ms-1'}>{niceReviewFormat()} </dd>
                    </dl>
                )}
                <Button
                    variant="outline-info"
                    className={'text-primary'}
                    onClick={() => handleOnSelectReview(review)}
                >
                    {isPrimary ? 'Complete evaluation' : 'Verify sessions'}
                </Button>
            </CardBody>
        </Card>
    );
};

export default ReviewsBlock;
